import { useState, useRef, useCallback } from "react";

interface UseTimerOptions {
  /**
   * Called when the timer is stopped (not reset).
   * Receives the elapsed seconds at the moment of stopping.
   */
  onStop?: (elapsedSeconds: number) => void;
}

interface UseTimerReturn {
  /** Current elapsed time in seconds */
  elapsedSeconds: number;
  /** Whether the timer is currently running */
  isRunning: boolean;
  /** Start the timer (no-op if already running) */
  start: () => void;
  /** Stop the timer and trigger onStop callback */
  stop: () => void;
  /** Reset the timer to 0 without triggering onStop */
  reset: () => void;
}

/**
 * Custom hook for managing a timer with start/stop/reset functionality.
 *
 * @example
 * ```tsx
 * const { elapsedSeconds, isRunning, start, stop, reset } = useTimer({
 *   onStop: (seconds) => {
 *     const hours = Math.ceil(seconds / 360) / 10;
 *     openAddEntryDialog(hours);
 *   }
 * });
 * ```
 */
export function useTimer({ onStop }: UseTimerOptions = {}): UseTimerReturn {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const clearTimerInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (isRunning) return; // Already running, no-op

    setIsRunning(true);
    intervalRef.current = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
  }, [isRunning]);

  const stop = useCallback(() => {
    clearTimerInterval();
    setIsRunning(false);

    // Get current elapsed seconds and trigger callback
    // We need to use a ref or read state synchronously here
    // Since setElapsedSeconds is async, we pass the current value
    setElapsedSeconds((currentSeconds) => {
      if (onStop) {
        onStop(currentSeconds);
      }
      return currentSeconds; // Don't change the value
    });
  }, [clearTimerInterval, onStop]);

  const reset = useCallback(() => {
    clearTimerInterval();
    setIsRunning(false);
    setElapsedSeconds(0);
  }, [clearTimerInterval]);

  return {
    elapsedSeconds,
    isRunning,
    start,
    stop,
    reset,
  };
}
